import type { SxProps, Theme } from "@mui/material";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { ListKit } from "@tiptap/extension-list";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Strike } from "@tiptap/extension-strike";
import TextAlign from "@tiptap/extension-text-align";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ContentCover } from "components/content-cover";
import DOMPurify from "dompurify";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ChipInsertMenu } from "./ChipInsertMenu";
import { EDITOR_DEFAULTS } from "./constants";
import { EmailEditorToolbar } from "./EmailEditorToolbar";
import { HtmlCodeBlockModal } from "./extensions/html-code-block/HtmlCodeBlockModal";
import { HtmlCodeBlock } from "./extensions/html-code-block/htmlCodeBlockExtension";
import { VariableChip } from "./extensions/variable-chip/variableChipExtension";
import { createVariableChipSuggestion } from "./extensions/variable-chip/variableChipSuggestion";
import type {
  ChipOption,
  TipTapChipEditorProps,
  TipTapChipEditorRef,
} from "./types";

// ─── Helper: Merge base sx with override sx ────────────────────────

const mergeSx = (
  base: SxProps<Theme>,
  override?: SxProps<Theme>,
): SxProps<Theme> => {
  if (!override) return base;
  const baseArr = Array.isArray(base) ? base : [base];
  const overrideArr = Array.isArray(override) ? override : [override];
  return [...baseArr, ...overrideArr];
};

// ─── Helper: Read-only HTML section ─────────────────────────────────

interface ReadOnlyHtmlSectionProps {
  content: string;
  testId?: string;
}

const ReadOnlyHtmlSection = ({
  content,
  testId,
}: ReadOnlyHtmlSectionProps): JSX.Element | null => {
  const theme = useTheme();

  if (!content) return null;

  const sanitized = DOMPurify.sanitize(content, {
    ADD_ATTR: ["data-value", "data-label", "data-id", "data-type", "data-code"],
  });

  return (
    <Box
      data-testid={testId}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
      dangerouslySetInnerHTML={{ __html: sanitized }}
      sx={{
        padding: "8px",
        backgroundColor: theme.palette.neutral["005"],
        ...theme.typography["body-medium"],
        color: theme.palette.lightBg.medium,
        lineHeight: "26px",
        pointerEvents: "none",
        userSelect: "none",
      }}
    />
  );
};

// ─── Main Component ─────────────────────────────────────────────────

export const TipTapChipEditor = forwardRef<
  TipTapChipEditorRef,
  TipTapChipEditorProps
>(
  (
    {
      name,
      control,
      value,
      onChange,
      placeholder = EDITOR_DEFAULTS.placeholder,
      readOnly = false,
      minHeight = EDITOR_DEFAULTS.minHeight,
      maxHeight = EDITOR_DEFAULTS.maxHeight,
      showToolbar = false,
      toolbarPosition = EDITOR_DEFAULTS.toolbarPosition,
      endAdornment,
      overlay,
      chipOptions = [],
      showChipInsertButton = true,
      showHtmlInsert = true,
      headerContent = "",
      footerContent = "",
      signatureContent = "",
      onImageUpload,
      error = false,
      errorMessage,
      containerSx,
      wrapperSx,
      uploadOverlaySx,
      scrollContainerSx,
      editorAreaSx,
      editorContentSx,
      chipInsertSx,
      errorSx,
    },
    ref,
  ) => {
    const theme = useTheme();
    const [isHtmlModalOpen, setIsHtmlModalOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Track the last emitted HTML to prevent infinite loops
    const lastEmittedHtmlRef = useRef<string>("");
    // Track if the update came from internal editor changes
    const isInternalUpdateRef = useRef(false);

    // Ref for chip options (used by suggestion system to always get latest)
    const chipOptionsRef = useRef<ChipOption[]>(chipOptions);
    useEffect(() => {
      chipOptionsRef.current = chipOptions;
    }, [chipOptions]);

    // ─── TipTap Editor ─────────────────────────────────────────

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: false,
          codeBlock: false,
          bulletList: false,
          orderedList: false,
          listItem: false,
          hardBreak: false,
        }),
        Strike,
        Underline,
        ListKit,
        TextAlign.configure({
          types: ["paragraph"],
          alignments: ["left", "center", "right"],
          defaultAlignment: "left",
        }),
        Placeholder.configure({
          placeholder: () => placeholder,
        }),
        // `inclusive: false` so typing right after a link is normal text, not a
        // continuation of the link (it's a mark-level prop, set via extend()).
        Link.extend({ inclusive: false }).configure({
          openOnClick: false,
          autolink: true,
        }),
        Image.configure({
          inline: true,
          HTMLAttributes: {
            style: "max-width: 100px; height: auto; border-radius: 4px;",
          },
        }),
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        VariableChip.configure({
          suggestion: createVariableChipSuggestion(chipOptionsRef),
        }),
        HtmlCodeBlock,
      ],
      editable: !readOnly,
      content: value || "",
      editorProps: {
        handlePaste: (view, event) => {
          if (!onImageUpload) return false;

          const items = event.clipboardData?.items;
          if (!items) return false;

          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.indexOf("image") !== -1) {
              event.preventDefault();
              const file = item.getAsFile();
              if (!file) continue;

              handleImageUpload(file);
              return true;
            }
          }
          return false;
        },
      },
      onUpdate: ({ editor: ed }) => {
        const html = ed.getHTML();

        // Prevent emitting the same HTML twice (avoids infinite loops)
        if (html === lastEmittedHtmlRef.current) return;

        lastEmittedHtmlRef.current = html;
        isInternalUpdateRef.current = true;

        onChange?.(html);
      },
    });

    // ─── Image upload handler ─────────────────────────────────

    const handleImageUpload = useCallback(
      async (file: File) => {
        if (!onImageUpload || !editor) return;

        try {
          setIsUploading(true);
          const url = await onImageUpload(file);
          editor.chain().focus().setImage({ src: url }).run();
        } catch (error: unknown) {
          if (error instanceof Error) {
            console.error("Image upload failed:", error.message);
          }
        } finally {
          setIsUploading(false);
        }
      },
      [onImageUpload, editor],
    );

    // ─── Sync editor with external value changes ──────────────

    useEffect(() => {
      if (!editor) return;
      if (isInternalUpdateRef.current) {
        isInternalUpdateRef.current = false;
        return;
      }

      const currentHtml = editor.getHTML();
      if (value !== undefined && value !== currentHtml) {
        lastEmittedHtmlRef.current = value;
        editor.commands.setContent(value, { emitUpdate: false });
      }
    }, [editor, value]);

    // ─── Sync editable state ──────────────────────────────────

    useEffect(() => {
      if (editor) {
        editor.setEditable(!readOnly);
      }
    }, [editor, readOnly]);

    // ─── Force placeholder re-render when placeholder changes ──

    useEffect(() => {
      if (editor) {
        editor.view.dispatch(editor.state.tr);
      }
    }, [editor, placeholder]);

    // ─── HTML code block insertion ────────────────────────────

    const handleOpenHtmlModal = useCallback(() => {
      setIsHtmlModalOpen(true);
    }, []);

    const handleInsertHtmlCode = useCallback(
      (code: string) => {
        if (!editor) return;
        editor.chain().focus().insertHtmlCodeBlock({ code }).run();
        setIsHtmlModalOpen(false);
      },
      [editor],
    );

    // ─── Imperative handle ────────────────────────────────────

    useImperativeHandle(ref, () => ({
      getEditor: () => editor,
      clear: () => {
        editor?.commands.clearContent();
      },
      insertImage: (url: string) => {
        editor?.chain().focus().setImage({ src: url }).run();
      },
      getHTML: () => editor?.getHTML() || "",
    }));

    // ─── Render ───────────────────────────────────────────────

    const showChipButton =
      showChipInsertButton && chipOptions.length > 0 && !readOnly;

    const toolbar = (
      <Stack direction="row" sx={{ alignItems: "center" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EmailEditorToolbar editor={editor} />
        </Box>
        {endAdornment}
      </Stack>
    );

    return (
      <Stack gap="4px" sx={containerSx}>
        {/* `overlay` covers the toolbar and editor box at their size; the
            editor stays mounted underneath. */}
        <ContentCover
          cover={overlay}
          sx={mergeSx({ position: "relative" }, wrapperSx)}
        >
          {/* Upload overlay */}
          {isUploading && (
            <Box
              sx={mergeSx(
                {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                  borderRadius: "12px",
                },
                uploadOverlaySx,
              )}
            />
          )}

          {/* Toolbar (top position) */}
          {showToolbar && toolbarPosition === "top" && toolbar}

          {/* Scrollable editor container */}
          <Box
            sx={mergeSx(
              {
                maxHeight: maxHeight,
                overflow: "auto",
                border: error
                  ? `1px solid ${theme.palette.error.main}`
                  : `1px solid ${theme.palette.neutral["015"]}`,
                // borderRadius: "12px",
              },
              scrollContainerSx,
            )}
          >
            {/* Header content */}
            {headerContent && (
              <ReadOnlyHtmlSection
                content={headerContent}
                testId="email-editor-header"
              />
            )}

            {/* Editor area with chip insert button */}
            <Box
              sx={mergeSx(
                {
                  display: "flex",
                  flexDirection: "row",
                  backgroundColor: readOnly
                    ? theme.palette.neutral.main
                    : theme.palette.neutral["005"],
                },
                editorAreaSx,
              )}
            >
              <Box
                sx={mergeSx(
                  {
                    flex: 1,
                    minHeight,
                    "& .tiptap": {
                      outline: "none",
                      minHeight,
                      ...theme.typography["body-medium"],
                      padding: "8px",
                      wordBreak: "break-word",
                      lineHeight: "26px",
                    },
                    "& .tiptap p.is-editor-empty:first-of-type::before": {
                      content: "attr(data-placeholder)",
                      color: theme.palette.lightBg.low,
                      float: "left",
                      height: 0,
                      pointerEvents: "none",
                    },
                    // Variable chip styling (in-editor)
                    "& .tiptap .variable-chip-nodeview": {
                      display: "inline",
                    },
                    // Image styling
                    "& .tiptap img": {
                      maxWidth: "100px",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: "4px",
                    },
                    // List styling
                    "& .tiptap ul, & .tiptap ol": {
                      paddingLeft: "20px",
                    },
                    // Blockquote styling
                    "& .tiptap blockquote": {
                      borderLeft: `3px solid ${theme.palette.neutral["015"]}`,
                      paddingLeft: "12px",
                      margin: "4px 0",
                      color: theme.palette.lightBg.medium,
                    },
                  },
                  editorContentSx,
                )}
              >
                <EditorContent editor={editor} />
              </Box>

              {/* Chip insert button */}
              {showChipButton && (
                <Box
                  sx={mergeSx(
                    {
                      padding: "2px",
                      position: "sticky",
                      top: 0,
                      alignSelf: "flex-start",
                      zIndex: 1,
                    },
                    chipInsertSx,
                  )}
                >
                  <ChipInsertMenu
                    editor={editor}
                    chipOptions={chipOptions}
                    showHtmlInsert={showHtmlInsert}
                    onInsertHtmlCode={handleOpenHtmlModal}
                  />
                </Box>
              )}
            </Box>

            {/* Signature content */}
            {signatureContent && (
              <ReadOnlyHtmlSection
                content={signatureContent}
                testId="email-editor-signature"
              />
            )}

            {/* Footer content */}
            {footerContent && (
              <ReadOnlyHtmlSection
                content={footerContent}
                testId="email-editor-footer"
              />
            )}
          </Box>

          {/* Toolbar (bottom position) */}
          {showToolbar && toolbarPosition === "bottom" && toolbar}
        </ContentCover>

        {/* Error message */}
        {error && (
          <Typography
            ml="14px"
            variant="body-small"
            sx={mergeSx({ color: theme.palette.error.main }, errorSx)}
          >
            {errorMessage || "Required"}
          </Typography>
        )}

        {/* HTML code block insert modal */}
        {isHtmlModalOpen && (
          <HtmlCodeBlockModal
            open={isHtmlModalOpen}
            onClose={() => setIsHtmlModalOpen(false)}
            code=""
            onSave={handleInsertHtmlCode}
            onDelete={() => setIsHtmlModalOpen(false)}
          />
        )}
      </Stack>
    );
  },
);

TipTapChipEditor.displayName = "TipTapChipEditor";
